// FILE: src/components/PasswordStrength.jsx
// Live strength meter under a new-password field: a bar in four steps
// plus the level in words (Too weak / Weak / Good / Strong), announced
// politely as it changes. Guidance only: the password rules
// (PasswordRules, the server's policy) decide what is accepted.
import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import { passwordStrength } from "../utils/passwordPolicy";

const TONES = [null, "error", "warning", "info", "success"];
const ICONS = [null, ErrorOutlineIcon, WarningAmberOutlinedIcon, CheckCircleOutlineIcon, VerifiedUserOutlinedIcon];

export default function PasswordStrength({ password = "", id = "password-strength" }) {
  const { level, label, tip } = passwordStrength(password);
  const tone = TONES[level];
  const Icon = ICONS[level];
  return (
    <Box id={id} data-testid="password-strength" data-level={level} sx={{ mt: 1.5 }}>
      <LinearProgress
        variant="determinate"
        value={level * 25}
        color={tone || "primary"}
        aria-label="Password strength"
        aria-valuetext={label || "Nothing typed yet"}
      />
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", mt: 1 }}>
        {Icon && <Icon aria-hidden fontSize="small" sx={{ color: `${tone}.main` }} />}
        <Typography id={`${id}-status`} aria-live="polite" aria-atomic="true" sx={{ fontWeight: 600 }}>
          {label ? `Password strength: ${label}` : "Password strength: type a password to see it"}
        </Typography>
      </Stack>
      {tip && <Typography sx={{ color: "text.secondary", mt: 0.5 }}>{tip}</Typography>}
    </Box>
  );
}
